import { useEffect, useRef, useState } from "react";
import type { ResourceKind } from "./types";

export interface LookedUpUser {
  id: number;
  name: string;
  email: string;
  inCurrentOrg: boolean;
  isOrgAdmin: boolean;
  hasViewAllReports: boolean;
  avatarUrl: string;
  // The user's existing org membership role value (system_key or id).
  // Lets the access popover pre-select their current role rather than
  // defaulting to the resource's first-position role.
  orgRoleValue?: string | null;
}

export type ImplicitAccessReason = "admin" | "view_all";

export type LookupState =
  | { kind: "idle" }
  | { kind: "looking" }
  | { kind: "known"; user: LookedUpUser }
  | { kind: "unknown"; email: string }
  | { kind: "already_in_list"; granteeId: number }
  | { kind: "implicit_access"; user: LookedUpUser; reason: ImplicitAccessReason };

interface Options {
  lookupUrl: string;
  email: string;
  // Caller supplies the current grantee list so we can short-circuit to
  // "already_in_list" without touching the network when the typed email
  // already has access.
  granteeEmailToId: ReadonlyMap<string, number>;
  // Resource being granted access. The reports:view_all permission
  // implies access only to reports — on the event widget it's
  // unrelated to event access and must NOT be treated as implicit.
  resourceKind: ResourceKind;
  // Optional resource event id — when set, lookup asks the server to
  // also surface event-level permissions (e.g. reports:view_all) so the
  // hook can derive "implicit_access" for users who already see the
  // resource without needing a grant.
  eventId?: number | string;
  debounceMs?: number;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Debounced email-lookup hook. Cancels in-flight requests on each new
// keystroke. Returns one of five states the popover renders against.
//
// Short-circuits before fetching:
//   - email blank or invalid format → idle
//   - email already in the grantee list → already_in_list (no fetch)
export function useEmailLookup({
  lookupUrl,
  email,
  granteeEmailToId,
  resourceKind,
  eventId,
  debounceMs = 300,
}: Options): LookupState {
  const [state, setState] = useState<LookupState>({ kind: "idle" });
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    abortRef.current?.abort();

    const trimmed = email.trim().toLowerCase();
    if (trimmed === "") {
      setState({ kind: "idle" });
      return;
    }
    if (!EMAIL_RE.test(trimmed)) {
      setState({ kind: "idle" });
      return;
    }
    const existingGranteeId = granteeEmailToId.get(trimmed);
    if (existingGranteeId !== undefined) {
      setState({ kind: "already_in_list", granteeId: existingGranteeId });
      return;
    }

    setState({ kind: "looking" });
    const controller = new AbortController();
    abortRef.current = controller;

    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ email: trimmed, resource_kind: resourceKind });
        if (eventId !== undefined && eventId !== null) {
          // Avoid the literal `event_id` query param — EventScoped#current_event
          // treats that name as an event slug elsewhere in the controller stack.
          params.set("resource_event_id", String(eventId));
        }
        const res = await fetch(`${lookupUrl}?${params.toString()}`, {
          headers: { Accept: "application/json" },
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;

        if (res.status === 422) {
          setState({ kind: "idle" });
          return;
        }
        const body = await res.json();
        if (controller.signal.aborted) return;

        if (body.found && body.user) {
          const user: LookedUpUser = {
            id: body.user.id,
            name: body.user.name,
            email: body.user.email,
            inCurrentOrg: body.user.in_current_org,
            isOrgAdmin: !!body.user.is_org_admin,
            hasViewAllReports: !!body.user.event_permissions?.reports_view_all,
            avatarUrl: body.user.avatar_url,
            orgRoleValue: body.user.org_role_value ?? null,
          };
          if (user.isOrgAdmin) {
            setState({ kind: "implicit_access", user, reason: "admin" });
          } else if (resourceKind === "report" && user.hasViewAllReports) {
            setState({ kind: "implicit_access", user, reason: "view_all" });
          } else {
            setState({ kind: "known", user });
          }
        } else {
          setState({ kind: "unknown", email: trimmed });
        }
      } catch (err) {
        if ((err as Error).name === "AbortError") return;
        // Network error — fall back to unknown so the user can still
        // proceed with a name capture path.
        setState({ kind: "unknown", email: trimmed });
      }
    }, debounceMs);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [email, lookupUrl, granteeEmailToId, resourceKind, eventId, debounceMs]);

  return state;
}
